import { useEffect, useState } from "react";

import { apiUrl } from '../../../config/api';
const HOME_CACHE_KEY = 'homeData';
const HOME_CACHE_TS_KEY = 'homeDataTimestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutos

function normalizeListadoNombres(value) {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item) => {
            if (typeof item === "string") {
                return item.trim();
            }

            return String(item?.jugador || item?.tecnico || item?.nombre || "").trim();
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "es"));
}

function useHomeData() {
    const [cantidadPartidosTotal, setCantidadPartidosTotal] = useState([]);
    const [cantidadPartidosGanados, setCantidadPartidosGanados] = useState([]);
    const [cantidadPartidosPerdidos, setCantidadPartidosPerdidos] = useState([]);
    const [ultimoPartido, setUltimoPartido] = useState([]);
    const [unDiaComoHoy, setUnDiaComoHoy] = useState([]);
    const [enAguada, setEnAguada] = useState([]);
    const [enAguadaGanados, setEnAguadaGanados] = useState([]);
    const [enAguadaPerdidos, setEnAguadaPerdidos] = useState([]);

    const [partidosJugador, setPartidosJugador] = useState([]);
    const [ptsJugador, setPuntosJugador] = useState([]);
    const [rtJugador, setRtJugador] = useState([]);
    const [astJugador, setAstJugador] = useState([]);

    const [minPromJugador, setMinutosPromJugador] = useState([]);
    const [ptsPromJugador, setPuntosPromJugador] = useState([]);
    const [rtPromJugador, setRtPromJugador] = useState([]);
    const [astPromJugador, setAstPromJugador] = useState([]);

    const [listadoJugadores, setListadoJugadores] = useState([]);
    const [listadoTecnicos, setListadoTecnicos] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    const getPercentage = (numerador, denominador) => {
        if (!denominador) {
            return '0.0';
        }

        return ((numerador / denominador) * 100).toFixed(1);
    };

    useEffect(() => {
        document.title = 'Aguada Stats';

        const isReload = performance.getEntriesByType("navigation")?.[0]?.type === "reload";

        if (isReload) {
            sessionStorage.removeItem(HOME_CACHE_KEY);
            sessionStorage.removeItem(HOME_CACHE_TS_KEY);
        }

        const cachedTimestamp = sessionStorage.getItem(HOME_CACHE_TS_KEY);
        const now = Date.now();

        if (!isReload && cachedTimestamp && (now - Number(cachedTimestamp)) < CACHE_DURATION_MS) {
            try {
                const cached = JSON.parse(sessionStorage.getItem(HOME_CACHE_KEY));
                if (cached) {
                    setCantidadPartidosTotal(cached.cantidadPartidosTotal);
                    setCantidadPartidosGanados(cached.cantidadPartidosGanados);
                    setCantidadPartidosPerdidos(cached.cantidadPartidosPerdidos);
                    setUltimoPartido(cached.ultimoPartido);
                    setUnDiaComoHoy(cached.unDiaComoHoy);
                    setEnAguada(cached.enAguada);
                    setEnAguadaGanados(cached.enAguadaGanados);
                    setEnAguadaPerdidos(cached.enAguadaPerdidos);
                    setPartidosJugador(cached.partidosJugador);
                    setPuntosJugador(cached.ptsJugador);
                    setRtJugador(cached.rtJugador);
                    setAstJugador(cached.astJugador);
                    setMinutosPromJugador(cached.minPromJugador);
                    setPuntosPromJugador(cached.ptsPromJugador);
                    setRtPromJugador(cached.rtPromJugador);
                    setAstPromJugador(cached.astPromJugador);
                    setListadoJugadores(cached.listadoJugadores);
                    setListadoTecnicos(cached.listadoTecnicos);
                    setIsLoading(true);
                    return;
                }
            } catch {
                // cache corrupta, continuar con fetch
            }
        }

        Promise.all([
            fetch(apiUrl('home/cantidadPartidos')),
            fetch(apiUrl('ultimoPartido')),
            fetch(apiUrl('diaComoHoy')),
            fetch(apiUrl('home/enAguada')),
            fetch(apiUrl('home/records')),
            fetch(apiUrl('listado/listadoJugadores')),
            fetch(apiUrl('listado/listadoTecnicos'))
        ])
            .then(([resCantidadPartidos, resUltimoPartido, resUnDiaComoHoy, resEnAguada, resRecords, resListadoJugadores, resListadoTecnicos]) =>
                Promise.all([resCantidadPartidos.json(), resUltimoPartido.json(), resUnDiaComoHoy.json(), resEnAguada.json(), resRecords.json(), resListadoJugadores.json(), resListadoTecnicos.json()])
            )
            .then(([dataCantidadPartidos, dataUltimoPartido, dataUnDiaComoHoy, dataEnAguada, dataRecords, dataListadoJugadores, dataListadoTecnicos]) => {
                const newData = {
                    cantidadPartidosTotal: dataCantidadPartidos.total,
                    cantidadPartidosGanados: dataCantidadPartidos.ganados,
                    cantidadPartidosPerdidos: dataCantidadPartidos.perdidos,
                    ultimoPartido: dataUltimoPartido,
                    unDiaComoHoy: dataUnDiaComoHoy,
                    enAguada: dataEnAguada.enAguada,
                    enAguadaGanados: dataEnAguada.enAguadaGanados,
                    enAguadaPerdidos: dataEnAguada.enAguadaPerdidos,
                    partidosJugador: dataRecords.totales.partidosJugador,
                    ptsJugador: dataRecords.totales.ptsJugador,
                    rtJugador: dataRecords.totales.rtJugador,
                    astJugador: dataRecords.totales.astJugador,
                    minPromJugador: dataRecords.promedios.minPromJugador,
                    ptsPromJugador: dataRecords.promedios.ptsPromJugador,
                    rtPromJugador: dataRecords.promedios.rtPromJugador,
                    astPromJugador: dataRecords.promedios.astPromJugador,
                    listadoJugadores: normalizeListadoNombres(dataListadoJugadores),
                    listadoTecnicos: normalizeListadoNombres(dataListadoTecnicos),
                };

                setCantidadPartidosTotal(newData.cantidadPartidosTotal);
                setCantidadPartidosGanados(newData.cantidadPartidosGanados);
                setCantidadPartidosPerdidos(newData.cantidadPartidosPerdidos);
                setUltimoPartido(newData.ultimoPartido);
                setUnDiaComoHoy(newData.unDiaComoHoy);
                setEnAguada(newData.enAguada);
                setEnAguadaGanados(newData.enAguadaGanados);
                setEnAguadaPerdidos(newData.enAguadaPerdidos);
                setPartidosJugador(newData.partidosJugador);
                setPuntosJugador(newData.ptsJugador);
                setRtJugador(newData.rtJugador);
                setAstJugador(newData.astJugador);
                setMinutosPromJugador(newData.minPromJugador);
                setPuntosPromJugador(newData.ptsPromJugador);
                setRtPromJugador(newData.rtPromJugador);
                setAstPromJugador(newData.astPromJugador);
                setListadoJugadores(newData.listadoJugadores);
                setListadoTecnicos(newData.listadoTecnicos);
                setIsLoading(true);

                try {
                    sessionStorage.setItem(HOME_CACHE_KEY, JSON.stringify(newData));
                    sessionStorage.setItem(HOME_CACHE_TS_KEY, String(Date.now()));
                } catch {
                    // sessionStorage no disponible
                }
            });
    }, []);

    return {
        cantidadPartidosTotal,
        cantidadPartidosGanados,
        cantidadPartidosPerdidos,
        ultimoPartido,
        unDiaComoHoy,
        enAguada,
        enAguadaGanados,
        enAguadaPerdidos,
        partidosJugador,
        ptsJugador,
        rtJugador,
        astJugador,
        minPromJugador,
        ptsPromJugador,
        rtPromJugador,
        astPromJugador,
        listadoJugadores,
        listadoTecnicos,
        isLoading,
        getPercentage,
    };
}

export default useHomeData;
